import AdminPageHeader from '../../../components/admin/AdminPageHeader';
import ProfessionalProfileFields from '../../../components/common/ProfessionalProfileFields';
import AccountSecurityPanel from '../../../components/accountSecurity/AccountSecurityPanel';
import { saveOwnProfile, securityError } from '../../../services/accountSecurityService';
// pages/Profile/ProfileLandingPage.tsx — Co Marca — estilo plataforma dashboard
import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import {
    Camera, Mail,
    User, Shield, CheckCircle2, Home, ChevronRight,
    Phone, CreditCard, CalendarCheck, Clock, XCircle,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { fetchCitas, Cita } from '../../../services/citasService';

const colors = {
    primary: 'var(--ad-primary, #3564db)',
    primaryDark: 'var(--ad-primary-dark, #8f1f64)',
    secondary: 'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `


@keyframes fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(var(--ad-primary-rgb, 193, 52, 139),.3); }
    50%     { box-shadow:0 0 0 5px rgba(var(--ad-primary-rgb, 193, 52, 139),0); }
}
@keyframes spin { to { transform:rotate(360deg); } }

.pl-page   { animation: fadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.pl-tab-btn:hover:not(.active) { color: var(--ad-ink, #374151) !important; background: var(--ad-soft, #f8fafc) !important; }
.pl-input {
    width:100%; padding:10px 14px;
    border:1.5px solid var(--ad-line, #e5e7eb); border-radius:10px;
    font-size:.9rem; color:var(--ad-ink, #374151);
    font-family:'DM Sans',sans-serif;
    outline:none; background:#fafafa;
    transition:border-color .2s, box-shadow .2s;
    box-sizing:border-box;
}
.pl-input:focus {
    border-color:${colors.primary} !important;
    box-shadow:0 0 0 3px rgba(var(--ad-primary-rgb, 193, 52, 139),.10) !important;
    background:var(--ad-surface, #fff) !important;
}
.pl-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:10px 24px; font-size:.88rem; font-weight:800;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.pl-btn-primary:hover { transform:translateY(-1px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32); }
.pl-btn-primary:disabled { background:var(--ad-line, #e2e8f0); color:var(--ad-muted, #94a3b8); box-shadow:none; cursor:not-allowed; transform:none; }
.pl-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-soft, #f8fafc); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:10px 20px; font-size:.88rem; font-weight:700;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .2s;
}
.pl-btn-ghost:hover { background:var(--ad-line, #f1f5f9); }
.pl-stat-card { transition:all .22s cubic-bezier(.16,1,.3,1); }
.pl-stat-card:hover { transform:translateY(-3px); }
.pl-avatar-wrap:hover .pl-avatar-overlay { opacity:1 !important; }
@media (max-width:900px) {
    .pl-layout { flex-direction:column !important; }
    .pl-sidebar { width:100% !important; }
}
`;

// ── Stat types ────────────────────────────────────────────────
interface Stat { value: number; label: string; icon: React.ReactNode; color: string; }

const calcStatsPersonal = (citas: Cita[], userId: number): Stat[] => {
    const reg = citas.filter(
        (c) => (c as any).usuario_registrador === userId || (c as any).usuario_registrador_id === userId
    );
    return [
        { value: reg.length, label: 'Registradas', icon: <CalendarCheck size={16}/>, color:'var(--ad-link, #2854c5)' },
        { value: reg.filter((c) => c.estado_ejecucion === 'Finalizada').length, label: 'Finalizadas', icon: <CheckCircle2 size={16}/>, color:'var(--ad-link, #2854c5)' },
        { value: reg.filter((c) => c.estado_ejecucion === 'Cancelada' || c.estado === 'Cancelada').length, label: 'Canceladas', icon: <XCircle size={16}/>, color:'var(--ad-link, #2854c5)' },
    ];
};

const calcStatsCliente = (citas: Cita[], userId: number): Stat[] => {
    const own = citas.filter(
        (c) => Number(c.cliente) === userId || (c.clientes_asociados && c.clientes_asociados.includes(userId))
    );
    return [
        { value: own.length, label: 'Total Citas', icon: <CalendarCheck size={16}/>, color:'var(--ad-link, #2854c5)' },
        { value: own.filter((c) => c.estado_ejecucion === 'Finalizada').length, label: 'Finalizadas', icon: <CheckCircle2 size={16}/>, color:'var(--ad-link, #2854c5)' },
        { value: own.filter((c) => c.estado === 'Pendiente' || c.estado_ejecucion === 'PorComenzar').length, label: 'Pendientes', icon: <Clock size={16}/>, color: colors.accentDark },
    ];
};

// ── MAIN ─────────────────────────────────────────────────────
const ProfilePage = () => {
    const { user, setUser } = useAuth();

    const [activeTab, setActiveTab] = useState<'General' | 'Seguridad'>('General');

    // Imagen
    const [selectedImage, setSelectedImage] = useState<string>(user?.foto_url ?? '');
    const [selectedFile,  setSelectedFile]  = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Stats
    const blankStats: Stat[] = user?.perfil === 'personal'
        ? [
            { value:0, label:'Registradas', icon:<CalendarCheck size={16}/>, color:'var(--ad-link, #2854c5)' },
            { value:0, label:'Finalizadas',  icon:<CheckCircle2 size={16}/>, color:'var(--ad-link, #2854c5)' },
            { value:0, label:'Canceladas',   icon:<XCircle size={16}/>,      color:'var(--ad-link, #2854c5)' },
        ]
        : [
            { value:0, label:'Total Citas',  icon:<CalendarCheck size={16}/>, color:'var(--ad-link, #2854c5)' },
            { value:0, label:'Finalizadas',  icon:<CheckCircle2 size={16}/>, color:'var(--ad-link, #2854c5)' },
            { value:0, label:'Pendientes',   icon:<Clock size={16}/>,         color:colors.accentDark },
        ];
    const [stats, setStats] = useState<Stat[]>(blankStats);
    const [statsLoading, setStatsLoading] = useState(true);

    // General form
    const [firstName,     setFirstName]     = useState(user?.first_name ?? '');
    const [lastName,      setLastName]      = useState(user?.last_name  ?? '');
    const [telefono,      setTelefono]      = useState(user?.telefono   ?? '');
    const [ciNit,         setCiNit]         = useState(user?.ci_nit     ?? '');
    const [cargo,         setCargo]         = useState(user?.cargo      ?? '');
    const [perfilPublico, setPerfilPublico] = useState(user?.perfil_publico ?? false);
    const [savingGeneral, setSavingGeneral] = useState(false);
    const [generalMsg,    setGeneralMsg]    = useState<{ type:'success'|'error'; text:string }|null>(null);

    useEffect(() => {
        setCargo(user?.cargo ?? '');
        setPerfilPublico(user?.perfil_publico ?? false);
    }, [user?.id, user?.cargo, user?.perfil_publico]);

    useEffect(() => {
        if (user?.foto_url && !selectedFile) setSelectedImage(user.foto_url);
    }, [user?.foto_url, selectedFile]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            setStatsLoading(true);
            try {
                const citas = await fetchCitas();
                setStats(user.perfil === 'personal'
                    ? calcStatsPersonal(citas, user.id)
                    : calcStatsCliente(citas, user.id));
            } catch { /* ignore */ }
            finally { setStatsLoading(false); }
        })();
    }, [user]);

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024 || !['image/jpeg','image/png','image/webp'].includes(file.type)) {
            setGeneralMsg({type:'error',text:'Usa una foto JPG, PNG o WebP de hasta 5 MB.'});
            e.target.value = ''; return;
        }
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => { if (ev.target?.result) setSelectedImage(ev.target.result as string); };
        reader.readAsDataURL(file);
    };

    const handleSaveGeneral = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        setSavingGeneral(true);
        setGeneralMsg(null);
        try {
            const updated = await saveOwnProfile({first_name:firstName, last_name:lastName, telefono, ci_nit:ciNit, cargo:cargo.trim(), perfil_publico:perfilPublico ? 'true' : 'false', foto:selectedFile});
            const newFotoUrl = updated.foto_url;
            setUser(updated);
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            if (newFotoUrl) setSelectedImage(newFotoUrl);
            setGeneralMsg({ type:'success', text:'Cambios guardados correctamente.' });
        } catch (err) {
            setGeneralMsg({ type:'error', text:securityError(err) });
        } finally { setSavingGeneral(false); }
    };

    const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`.toUpperCase();

    return <div data-admin-page="true" className="luma-profile-settings">
      <AdminPageHeader eyebrow="CONFIGURACIÓN / CO MARCA GROUP" title="Mi perfil" description="Administra tu información personal y la seguridad de tu cuenta."/>
      <section className="cowork-profile-identity"><span className="luma-identity-avatar">{selectedImage?<img src={selectedImage} alt=""/>:initials}</span><div><p className="vx-section-kicker">TU CUENTA</p><h2>{user?.first_name} {user?.last_name}</h2><p>{user?.role} · {user?.email}</p></div><nav className="cowork-profile-links" aria-label="Apartados de mi perfil"><a href="#profile-personal"><User size={16}/>Información</a><a href="#profile-security"><Shield size={16}/>Seguridad</a></nav></section><div className="cowork-profile-columns">
      <section id="profile-personal" className="luma-resource-panel luma-settings-panel"><div className="luma-panel-heading"><div><h3><User size={19}/>Información personal</h3><p className="luma-detail-note">Actualiza tus datos y cómo apareces en la comunidad.</p></div><span className="luma-badge">Configuración</span></div>
        <form onSubmit={handleSaveGeneral}><div className="luma-form-grid">
          <label>Nombre<input required value={firstName} onChange={e=>setFirstName(e.target.value)}/></label>
          <label>Apellido<input required value={lastName} onChange={e=>setLastName(e.target.value)}/></label>
          <label>Correo electrónico<input type="email" readOnly value={user?.email||''}/></label>
          <label>Teléfono<input type="tel" value={telefono} onChange={e=>setTelefono(e.target.value)}/></label>
          {user?.perfil==='cliente'&&<label>CI / NIT<input value={ciNit} onChange={e=>setCiNit(e.target.value)}/></label>}
          {user?.perfil!=='cliente'&&<ProfessionalProfileFields cargo={cargo} onCargoChange={setCargo} perfilPublico={perfilPublico} onPerfilPublicoChange={setPerfilPublico}/>}
          <div><label htmlFor="profile-photo">Foto de perfil</label><div className="luma-profile-photo">{selectedImage?<img src={selectedImage} alt="Foto de perfil"/>:<span className="luma-identity-avatar">{initials}</span>}<input id="profile-photo" type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/webp" onChange={handleImageChange}/></div><p className="luma-detail-note">JPG, PNG o WebP · hasta 5 MB{selectedFile?' · Guarda para confirmar la imagen':''}</p></div>
        </div>{generalMsg&&<p role={generalMsg.type==='error'?'alert':'status'} className="luma-feedback">{generalMsg.text}</p>}
        <div className="luma-panel-actions"><button className="vx-button vx-button-primary" disabled={savingGeneral} type="submit">{savingGeneral?'Guardando…':'Guardar cambios'}</button></div></form>
      </section>
      <section id="profile-security" className="luma-resource-panel luma-settings-panel"><div className="luma-panel-heading"><div><h3><Shield size={19}/>Seguridad de la cuenta</h3><p className="luma-detail-note">Protege tu acceso y tus datos personales.</p></div></div><AccountSecurityPanel/></section></div>
      <details className="luma-resource-panel luma-settings-panel"><summary>Mi actividad</summary><div className="luma-metric-grid">{stats.map(item=><article className="luma-metric" key={item.label}><span>{item.label}</span><strong>{statsLoading?'—':item.value}</strong></article>)}</div></details>
    </div>;
};

export default ProfilePage;
